/**
 * 行政班「成绩总览」(博颂 2026-09-24:要能看所有班级所有学科的 GPA)。
 * 行 = 学生,列 = 本学期各门课程的最终成绩 / 等级;右侧综合 GPA =
 * Σ(绩点 × 学分) ÷ Σ学分,再乘行规分系数。园长、集团管理员、本班班主任可看。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { useHomeroomOverview, useSchoolOverview } from "../../api/gpa";
import { canManageSchool, useClasses, useOrgMe } from "../../api/school";

export function GpaOverviewPage(): JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const classes = useClasses();
  const orgMe = useOrgMe();
  const isManager = canManageSchool(orgMe.data);
  // 全校总表(园长/主任/集团管理员)是默认视图;班主任只有按行政班
  // 带了 class 参数(例如从班级页「成绩总览」点进来)就直接看这个班;园长/主任之前会落到全校总表。
  const mode = params.get("view") === "class" || params.has("class") || (!isManager && !orgMe.isPending) ? "class" : "school";
  const classId = params.get("class") ?? "";
  const [semester, setSemester] = useState<string | null>(params.get("semester"));
  useEffect(() => {
    if (mode === "class" && !classId && classes.data?.length) setParams({ view: "class", class: classes.data[0]!.id }, { replace: true });
  }, [mode, classId, classes.data, setParams]);
  const ov = useHomeroomOverview(mode === "class" ? classId || undefined : undefined, semester);
  const school = useSchoolOverview(mode === "school" && isManager, semester);
  const data = ov.data;

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-800">{t("gpa.overview.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("gpa.overview.hint")}</p>
      </header>
      {isManager && (
        <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm" role="tablist">
          {(["school", "class"] as const).map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m}
              onClick={() => { setSemester(null); setParams(m === "school" ? {} : { view: "class", class: classId || classes.data?.[0]?.id || "" }); }}
              className={`rounded-md px-3 py-1 ${mode === m ? "bg-sky-500 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`ov-mode-${m}`}>
              {t(`gpa.overview.mode.${m}`)}
            </button>
          ))}
        </div>
      )}
      {mode === "school" && isManager && <SchoolTable data={school.data} loading={school.isLoading} semester={semester} onSemester={setSemester} />}
      {mode === "class" && (<>
      <section className="mb-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="text-xs text-slate-600">
          {t("gpa.overview.homeroom")}
          <br />
          <select id="ov-class" value={classId} onChange={(e) => { setSemester(null); setParams({ view: "class", class: e.target.value }); }} className="rounded border border-slate-200 px-2 py-1">
            {(classes.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        {data && data.semesters.length > 0 && (
          <label className="text-xs text-slate-600">
            {t("gpa.overview.semester")}
            <br />
            <select id="ov-sem" value={data.semester ?? ""} onChange={(e) => setSemester(e.target.value)} className="rounded border border-slate-200 px-2 py-1">
              {data.semesters.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
      </section>
      {ov.isLoading && <p className="text-sm text-slate-500">{t("common.loading")}</p>}
      {ov.error && <p className="text-sm text-rose-600">{t("gpa.overview.denied")}</p>}
      {data && data.courses.length === 0 && <p className="text-sm text-slate-500">{t("gpa.overview.noCourses")}</p>}
      {data && data.courses.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs" data-testid="gpa-overview">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="sticky left-0 bg-slate-50 px-2 py-2 text-left">{t("gpa.grades.col.name")}</th>
                {data.courses.map((c) => (
                  <th key={c.id} className="px-2 py-2 text-center">
                    <Link to={`/classes/${c.id}/grades`} className="whitespace-nowrap hover:text-sky-600">{c.name}</Link>
                    <div className="whitespace-nowrap font-normal text-slate-400">
                      {c.course_type === "tier" ? `${t("classes.courses.tier")} · ` : ""}{c.credits != null ? t("gpa.overview.credits", { n: c.credits }) : t("gpa.overview.noCredits")}
                    </div>
                  </th>
                ))}
                <th className="whitespace-nowrap bg-sky-50 px-2 py-2 text-center">{t("gpa.overview.gpa")}</th>
                <th className="whitespace-nowrap px-2 py-2 text-center">{t("gpa.overview.coefficient")}</th>
                <th className="whitespace-nowrap bg-sky-50 px-2 py-2 text-center">{t("gpa.overview.gpaAdjusted")}</th>
                <th className="whitespace-nowrap px-2 py-2 text-center">{t("gpa.overview.weighted")}</th>
              </tr>
            </thead>
            <tbody>
              {data.students.map((s) => (
                <tr key={s.student_id} className="border-t border-slate-100">
                  <td className="sticky left-0 whitespace-nowrap bg-white px-2 py-1.5">
                    <Link to={`/students/${s.student_id}`} className="font-medium text-slate-800 hover:underline">{s.name_zh}</Link>
                    {s.name_en && <span className="ml-1 text-slate-400">{s.name_en}</span>}
                  </td>
                  {data.courses.map((c) => {
                    const cell = s.courses[c.id];
                    if (!cell) return <td key={c.id} className="px-2 py-1.5 text-center text-slate-200">·</td>;
                    return (
                      <td key={c.id} className="px-2 py-1.5 text-center tabular-nums">
                        {cell.final ?? <span className="text-slate-300">—</span>}
                        {cell.label && <div className="text-[10px] text-slate-400">{cell.label}</div>}
                      </td>
                    );
                  })}
                  <td className="bg-sky-50/50 px-2 py-1.5 text-center font-semibold tabular-nums">{s.gpa ?? "—"}</td>
                  <td className="px-2 py-1.5 text-center tabular-nums text-slate-500">× {s.coefficient.toFixed(2)}</td>
                  <td className="bg-sky-50/50 px-2 py-1.5 text-center font-semibold tabular-nums text-sky-800">{s.gpa_adjusted ?? "—"}</td>
                  <td className="px-2 py-1.5 text-center tabular-nums text-slate-600">{s.weighted_average ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <p className="mt-2 text-xs text-slate-400">{t("gpa.overview.formula")}</p>}
      </>)}
    </div>
  );
}

function SchoolTable({ data, loading, semester, onSemester }: { data?: import("../../api/gpa").SchoolOverview; loading: boolean; semester: string | null; onSemester: (s: string) => void }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<"class" | "gpa">("class");
  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (!data) return null;
  const rows = [...data.students].sort((a, b) =>
    sort === "gpa" ? (b.gpa_adjusted ?? -1) - (a.gpa_adjusted ?? -1) : a.class_name.localeCompare(b.class_name) || (a.student_code ?? "").localeCompare(b.student_code ?? ""));
  return (
    <div>
      <section className="mb-3 flex flex-wrap items-end gap-3 text-sm">
        {data.semesters.length > 0 && (
          <label className="text-xs text-slate-600">{t("gpa.overview.semester")}<br />
            <select id="ov-school-sem" value={semester ?? data.classes[0]?.semester ?? ""} onChange={(e) => onSemester(e.target.value)} className="rounded border border-slate-200 px-2 py-1">
              {data.semesters.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-slate-600">{t("gpa.overview.sort")}<br />
          <select id="ov-sort" value={sort} onChange={(e) => setSort(e.target.value as "class" | "gpa")} className="rounded border border-slate-200 px-2 py-1">
            <option value="class">{t("gpa.overview.sortClass")}</option>
            <option value="gpa">{t("gpa.overview.sortGpa")}</option>
          </select>
        </label>
      </section>
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="ov-class-summary">
        {data.classes.map((c) => (
          <Link key={c.class_id} to={`/gpa/overview?view=class&class=${c.class_id}`} className="rounded-lg border border-slate-200 bg-white p-3 hover:border-sky-300">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-slate-800">{c.class_name}</span>
              <span className="text-xs text-slate-400">{t("gpa.overview.classMeta", { n: c.students, c: c.courses })}</span>
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {t("gpa.overview.avgGpa")} <b className="tabular-nums text-sky-800">{c.avg_gpa ?? "—"}</b>
              {c.max_gpa != null && <span className="ml-2 text-xs text-slate-400">{t("gpa.overview.range", { min: c.min_gpa, max: c.max_gpa })}</span>}
              <span className="ml-2 text-xs text-slate-400">{t("gpa.overview.withGpa", { n: c.with_gpa })}</span>
            </div>
          </Link>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="gpa-school-overview">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="sticky left-0 bg-slate-50 px-2 py-2 text-left">{t("gpa.grades.col.name")}</th>
              <th className="whitespace-nowrap px-2 py-2 text-left">{t("gpa.overview.homeroom")}</th>
              {data.columns.map((c) => <th key={c} className="whitespace-nowrap px-2 py-2 text-center">{c}</th>)}
              <th className="whitespace-nowrap bg-sky-50 px-2 py-2 text-center">{t("gpa.overview.gpa")}</th>
              <th className="whitespace-nowrap px-2 py-2 text-center">{t("gpa.overview.coefficient")}</th>
              <th className="whitespace-nowrap bg-sky-50 px-2 py-2 text-center">{t("gpa.overview.gpaAdjusted")}</th>
              <th className="whitespace-nowrap px-2 py-2 text-center">{t("gpa.overview.weighted")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.student_id} className="border-t border-slate-100">
                <td className="sticky left-0 whitespace-nowrap bg-white px-2 py-1.5">
                  <Link to={`/students/${s.student_id}`} className="font-medium text-slate-800 hover:underline">{s.name_zh}</Link>
                  {s.name_en && <span className="ml-1 text-slate-400">{s.name_en}</span>}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">{s.class_name}</td>
                {data.columns.map((c) => {
                  const cell = s.by_course[c];
                  if (!cell) return <td key={c} className="px-2 py-1.5 text-center text-slate-200">·</td>;
                  return (
                    <td key={c} className="whitespace-nowrap px-2 py-1.5 text-center tabular-nums">
                      {cell.final ?? <span className="text-slate-300">—</span>}
                      {cell.label && <span className="ml-1 text-[10px] text-slate-400">{cell.label}</span>}
                    </td>
                  );
                })}
                <td className="bg-sky-50/50 px-2 py-1.5 text-center font-semibold tabular-nums">{s.gpa ?? "—"}</td>
                <td className="px-2 py-1.5 text-center tabular-nums text-slate-500">× {s.coefficient.toFixed(2)}</td>
                <td className="bg-sky-50/50 px-2 py-1.5 text-center font-semibold tabular-nums text-sky-800">{s.gpa_adjusted ?? "—"}</td>
                <td className="px-2 py-1.5 text-center tabular-nums text-slate-600">{s.weighted_average ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-400">{t("gpa.overview.formula")}</p>
    </div>
  );
}
